import { formatoMoneda } from './servicios';

// Genera un PDF de la cotización con la marca del negocio. jsPDF se importa de
// forma diferida (dynamic import) para no cargar la librería hasta que el
// cliente toca "Descargar PDF". Funciona en navegadores donde window.print()
// no hace nada (ej. el navegador interno de Instagram/WhatsApp).

function hexARgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || '').trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [219, 39, 119];
}

// Carga una imagen (logo) como dataURL. Best-effort: si falla, devuelve null.
async function imagenDataUrl(url) {
  try {
    const resp = await fetch(url, { mode: 'cors' });
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

export async function descargarCotizacionPdf(datos) {
  const { jsPDF } = await import('jspdf');
  const { cotizacion: c, items = [], negocio: n } = datos;
  const moneda = n?.moneda || c.moneda || 'L';
  const fmt = (v) => formatoMoneda(v, moneda);
  const [r, g, b] = hexARgb(n?.color_principal);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210, M = 15;
  let y = 0;

  // Encabezado con color de marca
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, W, 34, 'F');
  const logo = n?.logo_url ? await imagenDataUrl(n.logo_url) : null;
  let textoX = M;
  if (logo) {
    try { doc.addImage(logo, 'JPEG', M, 8, 18, 18, undefined, 'FAST'); textoX = M + 23; }
    catch { textoX = M; }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
  doc.text(String(n?.nombre || 'Cotización'), textoX, 17);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
  doc.text(`Cotización ${c.numero || ''}`, textoX, 26);

  y = 46;
  const salto = (necesita) => { if (y + necesita > 280) { doc.addPage(); y = 20; } };

  doc.setTextColor(70, 70, 70); doc.setFontSize(10);
  if (c.fecha_emision) { doc.text(`Emitida: ${c.fecha_emision}`, M, y); y += 6; }
  if (c.fecha_vencimiento) { doc.text(`Válida hasta: ${c.fecha_vencimiento}`, M, y); y += 6; }
  if (c.cliente_nombre) { doc.setFont('helvetica', 'bold'); doc.text(`Para: ${c.cliente_nombre}`, M, y); doc.setFont('helvetica', 'normal'); y += 6; }
  y += 2;

  doc.setDrawColor(225, 225, 225); doc.line(M, y, W - M, y); y += 7;

  // Renglones
  items.forEach((it) => {
    salto(16);
    const descLines = doc.splitTextToSize(String(it.descripcion || ''), 120);
    doc.setTextColor(20, 20, 20); doc.setFont('helvetica', 'bold'); doc.setFontSize(10.5);
    doc.text(descLines, M, y);
    doc.text(fmt(it.total), W - M, y, { align: 'right' });
    y += descLines.length * 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(150, 150, 150);
    let sub = `${Number(it.cantidad)} × ${fmt(it.precio_unitario)}`;
    if (Number(it.descuento) > 0) sub += ` · desc. ${fmt(it.descuento)}`;
    if (Number(it.impuesto_pct) > 0) sub += ` · imp. ${Number(it.impuesto_pct)}%`;
    doc.text(sub, M, y);
    y += 5;
    doc.setDrawColor(240, 240, 240); doc.line(M, y, W - M, y); y += 5;
  });

  // Totales
  const filaTotal = (etq, val, opts = {}) => {
    salto(8);
    doc.setFontSize(opts.grande ? 13 : 10);
    doc.setFont('helvetica', opts.fuerte ? 'bold' : 'normal');
    if (opts.color) doc.setTextColor(r, g, b); else doc.setTextColor(opts.fuerte ? 20 : 90, opts.fuerte ? 20 : 90, opts.fuerte ? 20 : 90);
    doc.text(etq, W - M - 60, y);
    doc.text(val, W - M, y, { align: 'right' });
    y += opts.grande ? 9 : 6;
  };
  y += 2;
  filaTotal('Subtotal', fmt(c.subtotal));
  if (Number(c.descuento) > 0) filaTotal('Descuento', `- ${fmt(c.descuento)}`);
  if (Number(c.impuesto) > 0) filaTotal('Impuesto', fmt(c.impuesto));
  salto(10); doc.setDrawColor(200, 200, 200); doc.line(W - M - 60, y - 2, W - M, y - 2); y += 3;
  filaTotal('Total', fmt(c.total), { fuerte: true, grande: true, color: true });
  if (Number(c.anticipo) > 0) {
    filaTotal('Anticipo', fmt(c.anticipo));
    filaTotal('Saldo pendiente', fmt(c.saldo), { fuerte: true });
  }

  // Información adicional / términos
  const bloque = (titulo, texto) => {
    if (!texto) return;
    y += 6; salto(20);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(90, 90, 90);
    doc.text(titulo, M, y); y += 5;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(60, 60, 60);
    const lineas = doc.splitTextToSize(String(texto), W - 2 * M);
    lineas.forEach((ln) => { salto(6); doc.text(ln, M, y); y += 5; });
  };
  bloque('Información adicional', c.notas);
  bloque('Términos y condiciones', c.terminos);

  // Pie
  const contacto = [n?.telefono, n?.instagram].filter(Boolean).join('  ·  ');
  doc.setFontSize(8); doc.setTextColor(160, 160, 160);
  if (contacto) doc.text(contacto, W / 2, 288, { align: 'center' });
  doc.text('Hecho con Kontrol · by Automatika.IA', W / 2, 293, { align: 'center' });

  const nombreArchivo = `Cotizacion-${(c.numero || 'kontrol').replace(/\s+/g, '')}.pdf`;
  try {
    doc.save(nombreArchivo);
  } catch {
    // Respaldo para navegadores restringidos: abrir el PDF en el visor.
    try { window.open(doc.output('bloburl'), '_blank'); } catch { /* nada más que hacer */ }
  }
}
